/* Native Shopify GET filtering. Product results are never filtered in the DOM. */
if (!customElements.get('ways-search-filters')) {
  customElements.define('ways-search-filters', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced) return;
      this.form = this.querySelector('[data-filter-form]');
      if (!this.form) return;
      this.panel = this.querySelector('[data-panel]');
      this.openButton = this.querySelector('[data-open]');
      this.closeButton = this.querySelector('[data-close]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.media = window.matchMedia('(max-width: 991px)');
      this.events = new AbortController();
      const signal = this.events.signal;
      this.isOpen = false;
      this.inertNodes = [];
      this.dataset.enhanced = 'true';
      this.preserveSearchState();
      this.onResize = () => {
        if (this.isOpen) this.close(false);
        this.panel.hidden = this.media.matches || this.dataset.hasFilters !== 'true';
      };
      this.media.addEventListener('change', this.onResize);
      this.onResize();
      this.openButton?.addEventListener('click', () => this.open(), {signal});
      this.closeButton?.addEventListener('click', () => this.close(), {signal});
      this.backdrop.addEventListener('click', () => this.close(), {signal});
      document.addEventListener('keydown', event => this.onKeydown(event), {signal});
      this.form.addEventListener('input', () => {
        this.checkPrice();
        this.updateSelectedCount();
      }, {signal});
      this.form.addEventListener('submit', event => {
        if (!this.checkPrice()) {
          event.preventDefault();
          this.form.reportValidity();
        }
      }, {signal});
      this.querySelector('[data-sort]')?.addEventListener('change', event => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        window.location.assign(url.href);
      }, {signal});
      window.addEventListener('pageshow', () => this.onResize(), {signal});
    }

    preserveSearchState() {
      const current = new URL(window.location.href);
      const optionKeys = ['options[prefix]', 'options[unavailable_products]'];
      for (const form of [this.form, this.querySelector('[data-query-form]'), this.querySelector('[data-sort-form]')]) {
        if (!form) continue;
        for (const key of optionKeys) {
          if (!current.searchParams.has(key)) continue;
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = key; input.value = current.searchParams.get(key);
          form.append(input);
        }
      }
      for (const link of this.querySelectorAll('[data-state-link]')) {
        const url = new URL(link.href, window.location.origin);
        const keys = link.dataset.stateLink === 'all' ? ['q', 'sort_by', ...optionKeys] : ['q', 'type', 'sort_by', ...optionKeys];
        for (const key of keys) {
          if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        }
        if (link.dataset.stateLink !== 'page') url.searchParams.delete('page');
        link.href = url.pathname + url.search + url.hash;
      }
    }

    open() {
      if (!this.media.matches || !this.openButton) return;
      this.isOpen = true;
      this.panel.hidden = false;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.backdrop.hidden = false;
      this.openButton.setAttribute('aria-expanded', 'true');
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.inertNodes = [];
      for (let branch = this.panel; branch.parentElement; branch = branch.parentElement) {
        for (const sibling of branch.parentElement.children) {
          if (sibling === branch || sibling === this.backdrop || sibling.contains(this.backdrop)) continue;
          if (!sibling.inert) { sibling.inert = true; this.inertNodes.push(sibling); }
        }
        if (branch.parentElement === document.body) break;
      }
      this.closeButton.focus();
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.inertNodes.forEach(node => { node.inert = false; });
      this.inertNodes = [];
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.backdrop.hidden = true;
      this.panel.hidden = this.media.matches;
      this.openButton?.setAttribute('aria-expanded', 'false');
      // Dismissal cancels unsubmitted changes; applied filters live in the URL.
      this.form.reset();
      this.checkPrice();
      this.updateSelectedCount();
      if (restoreFocus) this.openButton?.focus();
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href], button, input:not([type="hidden"]), select, summary, [tabindex="0"]')]
        .filter(element => !element.disabled && element.getClientRects().length && !element.closest('[inert]'));
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); this.panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    checkPrice() {
      const min = this.form.querySelector('[data-price-min]');
      const max = this.form.querySelector('[data-price-max]');
      if (!min || !max) return true;
      const invalid = min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value);
      max.setCustomValidity(invalid ? this.dataset.priceError : '');
      return !invalid;
    }

    updateSelectedCount() {
      let count = this.form.querySelectorAll('input[type="checkbox"]:checked').length;
      if ([...this.form.querySelectorAll('[data-price-min],[data-price-max]')].some(input => input.value !== '')) count += 1;
      const badge = this.querySelector('[data-selected-count]');
      if (badge) { badge.textContent = String(count); badge.hidden = count === 0; }
    }

    disconnectedCallback() {
      this.close(false);
      this.events?.abort();
      this.media?.removeEventListener('change', this.onResize);
      delete this.dataset.enhanced;
    }
  });
}
